import React from 'react';
import cx from 'classnames';
import { makeStyles } from '@material-ui/core/styles';

import AppBar from 'ha/ui/next/AppBar';
import Toolbar from 'ha/ui/next/Toolbar';
import Grid from 'ha/ui/next/Grid';
import units from 'ha/theme/units';
import LanguageSwitcher from 'ha/modules/LanguageSwitcher';
import Track from 'ha/components/track/SmartTrack';
import Button from 'ha/ui/next/Button';
import Divider from 'ha/ui/next/Divider';

import { usePage } from 'ha/modules/Page/contexts/PageContext';
import { LOGIN, REGISTER } from 'ha/constants/pageNames';
import NavBarLogo from './NavBarLogo';
import UnauthenticatedNavigation from './UnauthenticatedNavigation';
import AuthenticatedNavigation from '../containers/AuthenticatedNavigation.lazy';
import HowItWorksLink from './HowItWorksLink';
import PricingLink from './PricingLink';
import LearnMenu from './LearnMenu';
import useListYourPlaceCTA from '../hooks/useListYourPlaceCTA';

const useStyles = makeStyles(theme => ({
  // fixed width to allow centering via space-between
  fixedWidth: {
    [theme.breakpoints.up('lg')]: {
      minWidth: units.rem(23),
    },
    [theme.breakpoints.up('xl')]: {
      minWidth: '33%',
    },
  },
  darkBlue: {
    color: theme.palette.grey[700],
  },
  toolbar: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  userInfo: {
    [theme.breakpoints.down('md')]: {
      marginLeft: 'auto',
    },
  },
}));

interface Props {
  isAuthenticated: boolean;
}

export const NavBarDesktop: React.FC<Props> = ({ isAuthenticated }) => {
  const classes = useStyles();
  const ctaProps = useListYourPlaceCTA();
  const { name } = usePage();
  const isAuthPage = name === LOGIN || name === REGISTER;

  return (
    <AppBar
      position="relative"
      color="inherit"
      elevation={0}
      data-test-locator="NavBar Top Desktop"
    >
      <Toolbar className={classes.toolbar}>
        <Grid spacing={1} alignItems="center" justify="space-between" container>
          <Grid item className={classes.fixedWidth}>
            <NavBarLogo />
          </Grid>

          <Grid item>
            <div className={classes.darkBlue}>
              <HowItWorksLink />
              <PricingLink />
              <LearnMenu />
            </div>
          </Grid>

          <Grid item className={cx(classes.fixedWidth, classes.userInfo)}>
            <Grid container justify="flex-end" alignItems="center" spacing={2}>
              {!isAuthenticated && !isAuthPage && (
                <Grid item>
                  <UnauthenticatedNavigation />
                </Grid>
              )}
              {ctaProps.isVisible && (
                <Grid item>
                  <Track name="Clicked List your place in header" type="button">
                    <Button
                      variant="outlined"
                      color="primary"
                      data-test-locator="List Your Place CTA"
                      href={ctaProps.href}
                      rel={ctaProps.rel}
                    >
                      {ctaProps.label}
                    </Button>
                  </Track>
                </Grid>
              )}
              {isAuthenticated && (
                <Grid item>
                  <AuthenticatedNavigation />
                </Grid>
              )}
              <Grid item>
                <LanguageSwitcher className={classes.darkBlue} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Toolbar>
      <Divider />
    </AppBar>
  );
};

export default NavBarDesktop;
